import React from 'react';

import {
  ADD_APP_PAGE,
  APP_SETUP_PAGE,
  AppPresets,
  isEmbedAnyAppPreset,
} from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import {
  BreadCrumbBase,
  BreadCrumbItem,
} from 'src/legacy/components/UI/BreadCrumbBase';

import { useAppSetupPaths } from '../useAppSetupPaths';
import { useContentfulApps } from '../useContentfulApps';

import { useEditingApp } from './useEditingApp';

const SetupAppBreadCrumbs = () => {
  const { query } = React.useContext(RouteContext);
  const { getAppInfo } = useContentfulApps();
  const { moduleId, preset = '' } = query as {
    moduleId?: string;
    preset?: string;
  };
  // `cameFromAddApp` is true when the flow was launched from the redesigned Add
  // App experience, in which case `addAppReturnPath` is the validated origin to
  // root the trail at. See useAppSetupPaths for the flag/security details.
  const { addAppPagePath, cameFromAddApp, addAppReturnPath } =
    useAppSetupPaths();
  const { EnableAppSetupImprovements } = useFeatureFlags();
  const editingModule = useEditingApp();
  // The Partner App setup flow is surfaced as "Embed any app" in the redesigned
  // browse experience.
  const isEmbedAnyAppFlow =
    EnableAppSetupImprovements && isEmbedAnyAppPreset(preset);

  /**
   * Method to get the text value of last breadcrumb
   * @returns string
   */
  const getBreadcrumbTitle = React.useCallback(() => {
    if (isEmbedAnyAppPreset(preset)) return 'a Partner App';

    if (preset === AppPresets.CustomApp) return 'a Custom App';

    return getAppInfo(preset)?.name || '';
  }, [preset, getAppInfo]);

  /**
   * When moduleId === 'new' this is the create flow; otherwise it's the edit
   * flow for an existing module.
   */
  const breadCrumbsItems = React.useMemo<Array<BreadCrumbItem>>(() => {
    const appLibraryCrumb = {
      label: APP_SETUP_PAGE.label,
      path: APP_SETUP_PAGE.path,
    };
    // The standalone "Add an App" page only exists in the legacy (flag-off) flow.
    const legacyAddAppCrumb = EnableAppSetupImprovements
      ? []
      : [{ label: ADD_APP_PAGE.label, path: addAppPagePath }];

    // create module flow
    if (moduleId === 'new') {
      const leaf = {
        label: isEmbedAnyAppFlow
          ? 'Embed any app'
          : `Add ${getBreadcrumbTitle()}`,
        path: '',
      };

      // Redesigned Add App flow: "Add App" is the root (matching the sidebar
      // entry, linking back to the origin) and App Library is not part of the
      // trail.
      if (cameFromAddApp) {
        return [{ label: 'Add App', path: addAppReturnPath }, leaf];
      }

      return [appLibraryCrumb, ...legacyAddAppCrumb, leaf];
    }

    // edit module flow
    if (editingModule) {
      // if the module is disabled, it should show Enable instead of Edit.
      // This is the scenario when we're enabling a core module.
      if (editingModule.disabled) {
        return [
          appLibraryCrumb,
          ...legacyAddAppCrumb,
          { label: `Enable ${editingModule.label}`, path: '' },
        ];
      }

      return [
        appLibraryCrumb,
        { label: `Edit ${editingModule.label}`, path: '' },
      ];
    }

    return [];
  }, [
    moduleId,
    editingModule,
    EnableAppSetupImprovements,
    addAppPagePath,
    isEmbedAnyAppFlow,
    cameFromAddApp,
    addAppReturnPath,
    getBreadcrumbTitle,
  ]);

  return <BreadCrumbBase items={breadCrumbsItems} showOnlyTitleOnMobile />;
};

export default SetupAppBreadCrumbs;
